import { call, put, all } from 'redux-saga/effects';
import { DELETE, GET, PUT, POST, getHeaders, autoHideMessages } from 'src/services/ApiService';
import {
    formatMessage,
    setErrorScreenMessage,
    setSuccessfulScreenMessage,
    setScreenData,
    ACTIVE_SCREEN_KEY,
    hideDialog,
    hideDrawer,
    getScreenData,
    Store,
    showDialog,
} from 'cw-ui-core';
import Locale from 'src/i18n';
import {
    ADD_IDENTITY_PROVIDER_DRAWER,
    EXTERNAL_MFA,
    EXTERNAL_IDP,
    IDP_ICON_PATH,
    MFA_ICON_PATH,
    ADD_DUO_MFA_DRAWER,
    EDIT_IDENTITY_PROVIDER_USERS_TAB,
} from 'src/features/authentication-v2/constants';
import { loadUsers } from 'src/services/UsersService';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import size from 'lodash/size';
import { DUO_MFA } from 'src/constants';
import { UMS_LOADING_DIALOG } from 'src/features/users/components/UMSLoadingDialog/UMSLoadingDialog';
import { toArray, createQueryParams } from 'src/util/Util';
import { DEFAULT_LOADING_DIALOG } from 'src/components/DefaultLoadingDialog/DefaultLoadingDialog';
import { ALERT_TYPE } from 'src/asio-neon-components/Constants';
import {
    UNASSIGN_USERS_OPTIONS,
    YOUR_EXISTING_SECRET,
    AUTH_PROVIDER_EDIT_ASSIGNED_USERS_DIALOG,
    EDIT_EXTERNAL_IDP,
} from 'src/features/authentication-v2/constants';
import setAlertScreenMessage from 'src/asio-neon-components/Utils';
import { authenticationApi } from 'src/features/authentication-v2/services/authenticationService';

export function* loadLoginProvidersWithoutUserCount(screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(GET, '/LoginProviders', getHeaders());

    // if login providers failed,
    if (!result.isSuccess) {
        const error = formatMessage(Locale.authenticationFailedGetLoginProviders);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield call(autoHideMessages);
        return;
    }

    // process data
    const processedData = map(result.response, idp => {
        return {
            ...idp,
            id: idp.providerId,
            category: EXTERNAL_IDP,
            icon: IDP_ICON_PATH,
            name: idp.clientName,
        };
    });

    yield put(setScreenData(['loginProviders'], processedData, screenKey));
}

export function* loadExternalMfaWithoutUserCount(screenKey = ACTIVE_SCREEN_KEY, params = {}) {
    const queryParams = createQueryParams(params);
    const endPoint = isEmpty(queryParams) ? '/ExternalMFAProviders' : `/ExternalMFAProviders?${queryParams}`;
    const result = yield call(GET, endPoint, getHeaders());

    // if mfa providers failed,
    if (!result.isSuccess) {
        const error = formatMessage(Locale.failedGetExternalMfa);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield call(autoHideMessages);
        return;
    }

    // process data
    const processedData = map(result.response, mfa => {
        return { ...mfa, id: mfa.mfaId, category: EXTERNAL_MFA, icon: MFA_ICON_PATH };
    });

    yield put(setScreenData(['mfaProviders'], processedData, screenKey));
}

export function* loadLoginProvidersAndExternalMfa(screenKey = ACTIVE_SCREEN_KEY) {
    yield put(setScreenData(['isProviderReady'], false, screenKey));
    const sagas = [];
    sagas.push(call(loadLoginProvidersWithoutUserCount, screenKey));
    sagas.push(call(loadExternalMfaWithoutUserCount, screenKey));
    yield all(sagas);
    yield put(setScreenData(['isProviderReady'], true, screenKey));
}

export function* loadUsersForProviders(screenKey = ACTIVE_SCREEN_KEY) {
    yield put(setScreenData(['isUsersReady'], false, screenKey));
    let isSuccess = true;
    const loginProviders = toArray(getScreenData(Store().getState().screen, ['loginProviders'], screenKey));
    const mfaProviders = toArray(getScreenData(Store().getState().screen, ['mfaProviders'], screenKey));
    if (isEmpty(loginProviders) && isEmpty(mfaProviders)) {
        return;
    }
    const loginProviderUsers = map(loginProviders, provider => {
        return call(GET, `/LoginProviders/${provider.scheme}/users`, getHeaders());
    });

    const mfaProviderUsers = map(mfaProviders, provider => {
        return call(GET, `/ExternalMFAProviders/${provider.mfaId}/users`, getHeaders());
    });

    const results = yield all(loginProviderUsers.concat(mfaProviderUsers));
    const updatedLoginProvidersWithUsersCount = [];
    const updatedMfaProvidersWithUsersCount = [];

    for (let i = 0; i < size(loginProviders); i += 1) {
        const result = results[i];
        isSuccess = isSuccess && result.isSuccess;
        updatedLoginProvidersWithUsersCount.push({ ...loginProviders[i], users: size(result.response) });
    }

    for (let i = 0; i < size(mfaProviders); i += 1) {
        const result = results[i + size(loginProviders)];
        isSuccess = isSuccess && result.isSuccess;
        updatedMfaProvidersWithUsersCount.push({ ...mfaProviders[i], users: size(result.response) });
    }

    if (!isSuccess) {
        yield put(setErrorScreenMessage(formatMessage(Locale.failedToGetProviderUsers), false, [], screenKey));
        yield call(autoHideMessages, screenKey);
    }

    yield put(setScreenData(['loginProviders'], updatedLoginProvidersWithUsersCount, screenKey));
    yield put(setScreenData(['mfaProviders'], updatedMfaProvidersWithUsersCount, screenKey));
    yield put(setScreenData(['isUsersReady'], true, screenKey));
}

export function* getProvidersWithUsers(screenKey = ACTIVE_SCREEN_KEY) {
    yield call(loadLoginProvidersAndExternalMfa, screenKey);
    yield call(loadUsersForProviders, screenKey);
}

export function* loadLoginProviders(screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(GET, '/LoginProviders', getHeaders());

    if (!result.isSuccess) {
        const error = formatMessage(Locale.authenticationFailedGetLoginProviders);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield call(autoHideMessages);
        return;
    }

    // ***** Commented but we need this code in upcoming stories to show user count ********

    // eslint-disable-next-line
    // for (const provider in result.response) {
    //     const resultscheme = yield call(GET, `/LoginProviders/${result.response[provider].scheme}/users`, getHeaders());
    //     if (!resultscheme.isSuccess) {
    //         const error = formatMessage(Locale.authenticationFailedGetLoginProviders);
    //         put(setErrorScreenMessage(error, false, result.errorMessages));
    //         yield call(autoHideMessages);
    //         return;
    //     }
    //     result.response[provider].usersCount = resultscheme.response.length;
    // }

    yield put(setScreenData(['loginProvidersForBulkExport'], result.response, screenKey));
}

export function* loadLoginProviderWithUsers(scheme, screenKey = ACTIVE_SCREEN_KEY) {
    yield put(showDialog(DEFAULT_LOADING_DIALOG));

    const result = yield call(GET, `/LoginProviders/${scheme}`, getHeaders());

    if (!result.isSuccess) {
        yield put(hideDialog(DEFAULT_LOADING_DIALOG));

        const error = formatMessage(Locale.authenticationFailedGetLoginProviders);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield call(autoHideMessages);
        return;
    }

    const usersResult = yield call(GET, `/LoginProviders/${scheme}/users`, getHeaders());

    if (!usersResult.isSuccess) {
        const error = formatMessage(Locale.authenticationFailedGetLoginProviders);
        yield put(setErrorScreenMessage(error, false, result.errorMessages, screenKey));
        yield call(autoHideMessages);
        return;
    }
    const userIds = usersResult.response.map(user => user.id);

    // copy selected users for update logic
    yield put(setScreenData(['initiallySelectedUsers'], userIds, screenKey));
    yield put(setScreenData(['selectedUsers'], userIds, screenKey));

    yield put(hideDialog(DEFAULT_LOADING_DIALOG));
    yield put(setScreenData(['initialIdentityProviderData'], result.response, screenKey));
    yield put(setScreenData(['identityProvider'], result.response, screenKey));

    // explicitly initializing description to have a valid comparison for each field
    if (!result.response.description) {
        yield put(setScreenData(['initialIdentityProviderData', 'description'], '', screenKey));
        yield put(setScreenData(['identityProvider', 'description'], '', screenKey));
    }
}

export function* loadMfaWithoutUsers(mfaId, screenKey = ACTIVE_SCREEN_KEY) {
    yield put(showDialog(DEFAULT_LOADING_DIALOG));

    const result = yield call(GET, `/ExternalMFAProviders/${mfaId}`, getHeaders());

    if (!result.isSuccess) {
        yield put(hideDialog(DEFAULT_LOADING_DIALOG));

        const error = formatMessage(Locale.authenticationFailedGetLoginProviders);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield call(autoHideMessages);
        return;
    }

    yield put(hideDialog(DEFAULT_LOADING_DIALOG));
    yield put(setScreenData(['initialIdentityProviderData'], result.response, screenKey));
    yield put(setScreenData(['identityProvider'], result.response, screenKey));

    // explicitly initializing description to have a valid comparison for each field
    if (!result.response.description) {
        yield put(setScreenData(['initialIdentityProviderData', 'description'], '', screenKey));
        yield put(setScreenData(['identityProvider', 'description'], '', screenKey));
    }
}

export function* loadLoginProvider(scheme, screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(GET, `/LoginProviders/${scheme}`, getHeaders());

    if (!result.isSuccess) {
        const error = formatMessage(Locale.authenticationFailedGetLoginProviders);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield call(autoHideMessages);
        return;
    }
    const resultscheme = yield call(GET, `/LoginProviders/${scheme}/users`, getHeaders());
    if (!resultscheme.isSuccess) {
        const error = formatMessage(Locale.authenticationFailedGetLoginProviders);
        yield put(setErrorScreenMessage(error, false, result.errorMessages, screenKey));
        yield call(autoHideMessages);
        return;
    }
    const users = resultscheme.response.map(user => user.id);
    yield put(setScreenData(['checkboxes', 'startingSelectedUsers'], users, screenKey));
    yield put(setScreenData(['checkboxes', 'selectedUsers'], users, screenKey));
    yield put(setScreenData(['identityProvider'], result.response, screenKey));
}

export function* loadExternalMfa(mfaId, screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(GET, `/ExternalMFAProviders/${mfaId}`, getHeaders());

    if (!result?.isSuccess) {
        const error = formatMessage(Locale.failedGetExternalMfa);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield call(autoHideMessages);
        return;
    }

    yield put(setScreenData([DUO_MFA], result.response, screenKey));
}

export function* loadLoginProviderUsers(scheme, screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(GET, `/LoginProviders/${scheme}/users`, getHeaders());

    if (!result.isSuccess) {
        const error = formatMessage(Locale.authenticationFailedGetLoginProviders);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield call(autoHideMessages);
        return;
    }

    const users = result.response.map(user => user.id);
    yield put(setScreenData(['checkboxes', 'startingSelectedUsers'], users, screenKey));
    yield put(setScreenData(['checkboxes', 'selectedUsers'], users, screenKey));
}

export function* loginProviderAddUsers(scheme, users, screenKey = ACTIVE_SCREEN_KEY) {
    if (users) {
        const result = yield call(POST, `/LoginProviders/${scheme}/users/addUsers`, getHeaders(), users);

        if (!result.isSuccess) {
            yield put(
                setAlertScreenMessage(
                    ALERT_TYPE.error,
                    '',
                    formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers),
                    true,
                    screenKey
                )
            );
            return [formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers), result.error];
        }
        if (screenKey === EDIT_EXTERNAL_IDP) {
            if (result.isSuccess) {
                yield put(
                    setAlertScreenMessage(
                        ALERT_TYPE.success,
                        '',
                        formatMessage(Locale.authenticationEditProviderSuccessfully),
                        true,
                        screenKey
                    )
                );
            }
        }
        return null;
    }
    return [formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers)];
}

export function* assignUsersToExternalIdP(scheme, users, onSuccess, onError, screenKey = ACTIVE_SCREEN_KEY) {
    if (users) {
        yield put(showDialog(DEFAULT_LOADING_DIALOG));

        const result = yield call(POST, `/LoginProviders/${scheme}/users/addUsers`, getHeaders(), users);

        if (!result.isSuccess) {
            yield call(onError);
            yield put(hideDialog(DEFAULT_LOADING_DIALOG));
            setErrorScreenMessage(formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers), false, [
                result.error,
            ]);

            yield put(
                setAlertScreenMessage(
                    ALERT_TYPE.error,
                    '',
                    formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers),
                    true,
                    screenKey
                )
            );
            yield call(autoHideMessages);
            return null;
        }

        yield call(onSuccess);
        yield put(hideDialog(DEFAULT_LOADING_DIALOG));
        yield call(autoHideMessages);
        return null;
    }
    yield put(setErrorScreenMessage(formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers), false, []));
    yield call(autoHideMessages);
    yield put(
        setAlertScreenMessage(
            ALERT_TYPE.error,
            '',
            formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers),
            true,
            screenKey
        )
    );
    return null;
}

export function* loginProviderUsersRemoveAndResetPassword(scheme, users, screenKey = ACTIVE_SCREEN_KEY) {
    if (users) {
        const result = yield call(POST, `/LoginProviders/${scheme}/users/removeAndResetPassword`, getHeaders(), users);

        if (!result.isSuccess) {
            yield put(
                setAlertScreenMessage(
                    ALERT_TYPE.error,
                    '',
                    formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers),
                    true,
                    screenKey
                )
            );
            return [formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers), ...result.errorMessages];
        }
        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.success,
                '',
                formatMessage(Locale.authenticationEditProviderSuccessfully),
                true,
                screenKey
            )
        );
        return null;
    }
    return [formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers)];
}

export function* loginProviderUsersRemoveAndDisableUsers(scheme, users, screenKey = ACTIVE_SCREEN_KEY) {
    if (users) {
        const result = yield call(POST, `/LoginProviders/${scheme}/users/removeAndDisableUsers`, getHeaders(), users);

        if (!result.isSuccess) {
            yield put(
                setAlertScreenMessage(
                    ALERT_TYPE.error,
                    '',
                    formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers),
                    true,
                    screenKey
                )
            );
            return [formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers), ...result.errorMessages];
        }
        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.success,
                '',
                formatMessage(Locale.authenticationEditProviderSuccessfully),
                true,
                screenKey
            )
        );
        return null;
    }
    return [formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers)];
}

export function* loginProviderUsersRemoveAndDeleteUsers(scheme, users, screenKey = ACTIVE_SCREEN_KEY) {
    if (users) {
        const result = yield call(POST, `/LoginProviders/${scheme}/users/removeAndDeleteUsers`, getHeaders(), users);

        if (!result.isSuccess) {
            yield put(
                setAlertScreenMessage(
                    ALERT_TYPE.error,
                    '',
                    formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers),
                    true,
                    screenKey
                )
            );
            return [formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers), ...result.errorMessages];
        }
        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.success,
                '',
                formatMessage(Locale.authenticationEditProviderSuccessfully),
                true,
                screenKey
            )
        );
        return null;
    }
    return [formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers)];
}

export function* updateLoginProvider(loginProvider, screenKey = ACTIVE_SCREEN_KEY) {
    const providerAction = loginProvider.enabled
        ? formatMessage(Locale.enabled).toLowerCase()
        : formatMessage(Locale.disabled).toLowerCase();
    const result = yield call(PUT, `/LoginProviders/${loginProvider.scheme}`, getHeaders(), loginProvider);

    if (!result.isSuccess) {
        const errors = [];
        errors.concat(result.errorMessages);
        if (typeof result.error === 'string') errors.push(result.error);
        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.error,
                '',
                formatMessage(Locale.authenticationMethodActionFailed, {
                    action: providerAction,
                }),
                true,
                screenKey
            )
        );

        yield put(hideDialog(UMS_LOADING_DIALOG));
        return;
    }

    yield put(hideDrawer(screenKey));

    yield put(
        setAlertScreenMessage(
            ALERT_TYPE.success,
            '',
            formatMessage(Locale.authenticationMethodActionSuccessful, {
                action: providerAction,
            }),
            true,
            screenKey
        )
    );
    yield call(getProvidersWithUsers);
    yield put(hideDialog(UMS_LOADING_DIALOG));
    yield call(autoHideMessages);
}

export function* updateExternalMfa(mfaData, screenKey = ACTIVE_SCREEN_KEY, errorMessage = '', successMessage = '') {
    const result = yield call(PUT, `/ExternalMFAProviders/${mfaData.mfaId}`, getHeaders(), mfaData, screenKey);
    if (!result.isSuccess) {
        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.error,
                '',
                formatMessage(Locale.failedToActionExternalMfa, {
                    action: formatMessage(Locale.update),
                }),
                true,
                screenKey
            )
        );

        yield put(hideDialog(UMS_LOADING_DIALOG));
        yield call(autoHideMessages, screenKey);
        return;
    }
    yield put(hideDrawer(screenKey));
    yield call(getProvidersWithUsers);
    yield put(
        setAlertScreenMessage(
            ALERT_TYPE.success,
            '',
            formatMessage(Locale.successActionExternalMfa, {
                action: formatMessage(Locale.updated),
            }),
            true,
            screenKey
        )
    );
    yield put(hideDialog(UMS_LOADING_DIALOG));
    yield call(autoHideMessages);
}

export function* updateLoginProviderAndUsers(loginProvider, usersOptions, screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(PUT, `/LoginProviders/${loginProvider.scheme}`, getHeaders(), loginProvider);
    const isPermissionUnavailiable = result.status === 403 || result.status === 401;
    if (!result.isSuccess && !isPermissionUnavailiable) {
        const errors = [];
        errors.concat(result.errorMessages);
        if (typeof result.error === 'string') errors.push(result.error);

        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.error,
                '',
                formatMessage(Locale.authenticationFailedUpdateLoginProvider),
                true,
                screenKey
            )
        );

        yield put(hideDialog(UMS_LOADING_DIALOG));
        yield call(autoHideMessages, screenKey);
        return;
    }

    const { scheme } = isPermissionUnavailiable ? loginProvider : result?.response;

    if (usersOptions.addUsers.length !== 0) {
        const errors = yield call(loginProviderAddUsers, scheme, usersOptions.addUsers, screenKey);
        if (errors) {
            yield put(
                setErrorScreenMessage(
                    formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers),
                    false,
                    errors[1].split(),
                    EDIT_IDENTITY_PROVIDER_USERS_TAB
                )
            );
            yield put(hideDialog(UMS_LOADING_DIALOG));
            yield call(autoHideMessages, EDIT_IDENTITY_PROVIDER_USERS_TAB);
            return;
        }
    }

    if (usersOptions.removeUsers.length !== 0) {
        let errors;
        switch (usersOptions.removeUsersOption) {
            case UNASSIGN_USERS_OPTIONS.enableLocalLogin:
                errors = yield call(
                    loginProviderUsersRemoveAndResetPassword,
                    scheme,
                    usersOptions.removeUsers,
                    screenKey
                );
                break;
            case UNASSIGN_USERS_OPTIONS.disableUsers:
                errors = yield call(
                    loginProviderUsersRemoveAndDisableUsers,
                    scheme,
                    usersOptions.removeUsers,
                    screenKey
                );
                break;
            case UNASSIGN_USERS_OPTIONS.deleteUsers:
                errors = yield call(
                    loginProviderUsersRemoveAndDeleteUsers,
                    scheme,
                    usersOptions.removeUsers,
                    screenKey
                );
                break;
            case UNASSIGN_USERS_OPTIONS.moveUsers:
                errors = yield call(
                    loginProviderAddUsers,
                    usersOptions.moveUsersTargetScheme,
                    usersOptions.removeUsers,
                    screenKey
                );
                break;
            default:
                errors.push(formatMessage(Locale.unknownRemoveUsersOption));
                break;
        }

        if (errors) {
            yield put(
                setErrorScreenMessage(
                    formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers),
                    false,
                    null,
                    EDIT_IDENTITY_PROVIDER_USERS_TAB
                )
            );
            yield put(hideDialog(UMS_LOADING_DIALOG));
            yield call(autoHideMessages, EDIT_IDENTITY_PROVIDER_USERS_TAB);
            return;
        }
    }

    yield put(hideDrawer(screenKey));
    yield call(getProvidersWithUsers);

    yield put(
        setAlertScreenMessage(
            ALERT_TYPE.success,
            '',
            formatMessage(Locale.authenticationEditProviderSuccessfully),
            true,
            screenKey
        )
    );

    yield put(hideDialog(UMS_LOADING_DIALOG));
    yield put(hideDialog(screenKey));
    yield call(autoHideMessages);
}

export function* updateAssignedUsersForAuthProvider(scheme, usersOptions, screenKey = ACTIVE_SCREEN_KEY) {
    yield put(showDialog(DEFAULT_LOADING_DIALOG));

    // add newly selected users to auth provider
    if (usersOptions.addUsers.length !== 0) {
        const errors = yield call(loginProviderAddUsers, scheme, usersOptions.addUsers, screenKey);
        if (errors) {
            yield put(
                setErrorScreenMessage(
                    formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers),
                    false,
                    errors[1].split()
                )
            );
            yield put(hideDialog(DEFAULT_LOADING_DIALOG));
            yield call(autoHideMessages);
            return;
        }
    }

    if (usersOptions.removeUsers.length !== 0) {
        let errors;
        switch (usersOptions.removeUsersOption) {
            case UNASSIGN_USERS_OPTIONS.enableLocalLogin:
                errors = yield call(
                    loginProviderUsersRemoveAndResetPassword,
                    scheme,
                    usersOptions.removeUsers,
                    screenKey
                );
                break;
            case UNASSIGN_USERS_OPTIONS.disableUsers:
                errors = yield call(
                    loginProviderUsersRemoveAndDisableUsers,
                    scheme,
                    usersOptions.removeUsers,
                    screenKey
                );
                break;
            case UNASSIGN_USERS_OPTIONS.deleteUsers:
                errors = yield call(
                    loginProviderUsersRemoveAndDeleteUsers,
                    scheme,
                    usersOptions.removeUsers,
                    screenKey
                );
                break;
            case UNASSIGN_USERS_OPTIONS.moveUsers:
                errors = yield call(
                    loginProviderAddUsers,
                    usersOptions.moveUsersTargetScheme,
                    usersOptions.removeUsers,
                    screenKey
                );
                break;
            default:
                errors.push('Unknown remove users option!');
                break;
        }

        if (errors) {
            yield put(
                setErrorScreenMessage(formatMessage(Locale.authenticationFailedUpdateLoginProviderUsers), false, null)
            );
            yield put(hideDialog(DEFAULT_LOADING_DIALOG));
            yield call(autoHideMessages);
            return;
        }
    }

    // set initial selection after successful update
    yield put(setScreenData('initiallySelectedUsers', usersOptions.currentlySelectedUsers, screenKey));

    yield call(getProvidersWithUsers);

    yield call(autoHideMessages);
    if (usersOptions.removeUsers.length !== 0) {
        yield put(hideDialog(AUTH_PROVIDER_EDIT_ASSIGNED_USERS_DIALOG));
    }
    yield put(hideDialog(DEFAULT_LOADING_DIALOG));
    yield call(loadUsers, null, null, null, null, screenKey);
}

export function* updateExternalIdP(loginProvider, screenKey = ACTIVE_SCREEN_KEY) {
    yield put(showDialog(DEFAULT_LOADING_DIALOG));
    const scheme = loginProvider.scheme;
    const payload = getScreenData(Store().getState().screen, ['clientSecrets'], screenKey);
    const shouldUpdateClientSecret = loginProvider.secretRequired;
    let clientSecretError = false;
    if (payload === '' && shouldUpdateClientSecret) {
        const deleteClientSecretResponse = yield call(
            Store().dispatch,
            authenticationApi.endpoints.deleteClientSecret.initiate({
                scheme,
            })
        );
        clientSecretError = deleteClientSecretResponse.error ? true : false;
    }

    const result = yield call(PUT, `/LoginProviders/${scheme}`, getHeaders(), loginProvider);
    if (result.isSuccess) {
        yield put(setScreenData(['initialIdentityProviderData'], result.response, screenKey));
        if (payload !== YOUR_EXISTING_SECRET && payload !== '') {
            const saveOrUpdateClientSecretResponse = yield call(
                Store().dispatch,
                authenticationApi.endpoints.saveOrUpdateClientSecret.initiate({
                    scheme,
                    payload,
                })
            );
            clientSecretError = saveOrUpdateClientSecretResponse.error ? true : false;
        }
        const errorMsg = clientSecretError
            ? formatMessage(Locale.failedToUpdateClientSecrets)
            : formatMessage(Locale.authenticationEditProviderSuccessfully);
        const alertType = clientSecretError ? ALERT_TYPE.warning : ALERT_TYPE.success;
        yield put(setAlertScreenMessage(alertType, '', errorMsg, true, screenKey));
    } else {
        const errors = [];
        errors.concat(result.errorMessages);
        if (typeof result.error === 'string') errors.push(result.error);
        setErrorScreenMessage(formatMessage(Locale.authenticationFailedUpdateLoginProvider), false, null, screenKey);
        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.error,
                '',
                formatMessage(Locale.authenticationFailedUpdateLoginProvider),
                true,
                screenKey
            )
        );
        yield put(hideDialog(DEFAULT_LOADING_DIALOG));
        yield call(autoHideMessages, screenKey);
    }
    yield put(hideDialog(DEFAULT_LOADING_DIALOG));
    yield call(autoHideMessages);
}

function normalizeError(error) {
    let data;
    try {
        data = JSON.parse(error);
    } catch (parseError) {
        return [error];
    }
    return Object.entries(data).map(([key, value]) => (key.length !== 0 ? `${key} ` : '') + value);
}

export function* deleteLoginProvider(providerData, screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(DELETE, `/LoginProviders/${providerData?.scheme}`, getHeaders());

    if (result.isSuccess) {
        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.success,
                '',
                formatMessage(Locale.loginProviderDeletionSuccessful, {
                    action: formatMessage(Locale.deleted),
                }),
                true,
                screenKey
            )
        );

        yield call(getProvidersWithUsers);
        yield put(hideDialog(UMS_LOADING_DIALOG));

        yield call(autoHideMessages);
    } else {
        const errors = [].concat(result.errorMessages).concat(normalizeError(result.error));
        yield put(
            setErrorScreenMessage(
                formatMessage(Locale.authenticationMethodActionFailed, {
                    action: formatMessage(Locale.deleted),
                }),
                false,
                errors,
                screenKey
            )
        );
        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.error,
                '',
                formatMessage(Locale.authenticationMethodActionFailed, {
                    action: formatMessage(Locale.update),
                }),
                true,
                screenKey
            )
        );
    }
    yield put(hideDialog(UMS_LOADING_DIALOG));
    yield call(autoHideMessages);
}

export function* deleteExternalMfaProvider(providerData, screenKey = ACTIVE_SCREEN_KEY) {
    const { id } = providerData;
    const result = yield call(DELETE, `/ExternalMFAProviders/${id}`, getHeaders());
    if (!result?.isSuccess) {
        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.error,
                '',
                formatMessage(Locale.failedToActionExternalMfa, {
                    action: formatMessage(Locale.delete),
                }),
                true,
                screenKey
            )
        );

        yield put(hideDialog(UMS_LOADING_DIALOG));
        yield call(autoHideMessages);
        return;
    }

    yield put(
        setAlertScreenMessage(
            ALERT_TYPE.success,
            '',
            formatMessage(Locale.successActionExternalMfa, {
                action: formatMessage(Locale.deleted),
            }),
            true,
            screenKey
        )
    );

    yield call(getProvidersWithUsers);
    yield put(hideDialog(UMS_LOADING_DIALOG));
    yield call(autoHideMessages);
}

export function* addMfa(duoMfa, onSuccess, onError, screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(POST, '/ExternalMFAProviders/', getHeaders(), duoMfa);

    if (!result.isSuccess) {
        const errors = result.errorMessages ?? [];
        if (typeof result.error === 'string') errors.push(result.error);
        if (onError) {
            yield call(onError);
            yield put(
                setAlertScreenMessage(ALERT_TYPE.error, '', formatMessage(Locale.failedCreateMfa), true, screenKey)
            );
        } else {
            yield put(
                setAlertScreenMessage(ALERT_TYPE.error, '', formatMessage(Locale.failedCreateMfa), true, screenKey)
            );
        }
        yield put(hideDialog(UMS_LOADING_DIALOG));
        yield call(autoHideMessages);
        return;
    }

    if (onSuccess) yield call(onSuccess);
    yield put(hideDrawer(ADD_DUO_MFA_DRAWER));
    yield call(getProvidersWithUsers);
    yield put(setSuccessfulScreenMessage(formatMessage(Locale.mfaAdded), false, []));
    yield put(hideDialog(UMS_LOADING_DIALOG));
    yield call(autoHideMessages);
}

export function* addExternalIdPProvider(identityProvider, onSuccess, onError, screenKey = ACTIVE_SCREEN_KEY) {
    yield put(showDialog(DEFAULT_LOADING_DIALOG));

    const result = yield call(POST, '/LoginProviders/', getHeaders(), identityProvider);
    const payload = getScreenData(Store().getState().screen, ['clientSecrets'], screenKey);
    let clientSecretError = false;
    if (result.isSuccess) {
        // call mutataion to get client secret
        if (payload !== '') {
            const scheme = result.response.scheme;
            const saveOrUpdateClientSecretResponse = yield call(
                Store().dispatch,
                authenticationApi.endpoints.saveOrUpdateClientSecret.initiate({
                    scheme,
                    payload,
                })
            );
            clientSecretError = saveOrUpdateClientSecretResponse.error ? true : false;
            // handle errors
        }
        yield call(onSuccess);
        yield put(setScreenData(['identityProvider', 'scheme'], result.response.scheme, screenKey));
        const alertType = clientSecretError ? ALERT_TYPE.warning : ALERT_TYPE.success;
        const errorMsg = clientSecretError
            ? formatMessage(Locale.failedToUpdateClientSecrets)
            : formatMessage(Locale.authenticationProviderAdded);
        yield put(setAlertScreenMessage(alertType, '', errorMsg, true, screenKey));
    } else {
        const errors = result.errorMessages ?? [];
        if (typeof result.error === 'string') errors.push(result.error);
        yield call(onError);
        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.error,
                '',
                formatMessage(Locale.authenticationFailedIdPLoginProvider),
                true,
                screenKey
            )
        );
    }

    yield put(hideDialog(DEFAULT_LOADING_DIALOG));
    yield call(autoHideMessages);
}

export function* addLoginProvider(identityProvider, users) {
    const result = yield call(POST, '/LoginProviders/', getHeaders(), identityProvider);

    if (!result.isSuccess) {
        const errors = result.errorMessages ?? [];
        if (typeof result.error === 'string') errors.push(result.error);
        yield put(
            setErrorScreenMessage(
                formatMessage(Locale.authenticationFailedCreateLoginProvider),
                false,
                null,
                ADD_IDENTITY_PROVIDER_DRAWER
            )
        );
        yield put(hideDialog(UMS_LOADING_DIALOG));
        yield call(autoHideMessages);
        return;
    }

    const errors = yield call(loginProviderAddUsers, result.response.scheme, users);
    if (errors) {
        yield call(deleteLoginProvider, result.response.scheme);
        yield put(
            setErrorScreenMessage(
                formatMessage(Locale.authenticationFailedCreateLoginProvider),
                false,
                errors,
                ADD_IDENTITY_PROVIDER_DRAWER
            )
        );
        yield put(hideDialog(UMS_LOADING_DIALOG));
        yield call(autoHideMessages);
        return;
    }

    yield put(hideDrawer(ADD_IDENTITY_PROVIDER_DRAWER));
    yield call(getProvidersWithUsers);
    yield put(setSuccessfulScreenMessage(formatMessage(Locale.authenticationProviderAdded), false, []));
    yield put(hideDialog(UMS_LOADING_DIALOG));
    yield call(autoHideMessages);
}

export function* validateAuthority(authorityUrl, screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(POST, `/LoginProviders/ValidateAuthority`, getHeaders(), authorityUrl);

    if (!result.isSuccess) {
        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.error,
                '',
                formatMessage(Locale.authenticationTestConfigurationInvalid),
                true,
                screenKey
            )
        );

        yield call(autoHideMessages);
        return;
    }
    if (!result.response.isValid) {
        yield put(setScreenData(['isValidAuthorityUrl'], false, screenKey));

        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.error,
                '',
                formatMessage(Locale.authenticationTestConfigurationInvalid),
                true,
                screenKey
            )
        );

        yield call(autoHideMessages);
        return;
    }

    yield put(
        setAlertScreenMessage(
            ALERT_TYPE.success,
            '',
            formatMessage(Locale.authenticationTestConfigurationValid),
            true,
            screenKey
        )
    );

    yield put(setScreenData(['isValidAuthorityUrl'], true, screenKey));
    yield call(autoHideMessages);
}
